import React, { useEffect, useContext, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { GoalsContext } from '../State/GoalsContext';
import { IndicatorsContext } from '../State/IndicatorsContext';
import { GeneralDataContext } from '../State/GeneralDataContext';
import GoalGridDesktop from './GoalGridDesktop';
import GoalGridMobile from './GoalGridMobile';
import { goalTypeToUrl } from '../utils/goalTypeUtils';
import { buildHomeCopy } from '../utils/cmsCopy';
import './GoalGrid.css';

const GoalGrid = () => {
  const { goals, goalType, setSelectedGoal } = useContext(GoalsContext); // Removed fetchGoals - already called in App.js
  const { fetchAllIndicatorsByType, indicators, loading: indicatorsLoading } = useContext(IndicatorsContext);
  const { data: generalData } = useContext(GeneralDataContext);

  const navigate = useNavigate();
  
  // Fetch indicators when goalType changes (only once per goalType)
  const hasFetchedForGoalTypeRef = useRef(null);
  const goalsLoadedRef = useRef(false);
  
  // Track when goals are first loaded
  useEffect(() => {
    if (goals.length > 0) {
      goalsLoadedRef.current = true;
    }
  }, [goals.length]);
  
  useEffect(() => {
    // Only fetch if goals are loaded and we haven't fetched for this goalType yet
    if (goalsLoadedRef.current && goalType && hasFetchedForGoalTypeRef.current !== goalType) {
      hasFetchedForGoalTypeRef.current = goalType;
      fetchAllIndicatorsByType(goalType);
    }
  }, [goalType, fetchAllIndicatorsByType]);

  const handleGoalClick = (goal) => {
    setSelectedGoal(goal);
  };

  const handleIndicatorClick = (goalId, indicatorId) => {
    navigate(`/${goalTypeToUrl(goalType)}/goal/${goalId}/indicators/${indicatorId}`);
  };

  const isEU = goalType === 'eu';
  // generalData is already the ACF object (extracted from { acf: {...} } response)
  const title = isEU ? (generalData?.header_title_eu_sdg || 'EU Sustainable Development Goals for Kosovo') : (generalData?.header_title_global_sdg || 'Sustainable Development Goals for Kosovo');
  const subtitle = isEU
    ? (generalData?.report_subtitle_eu_sdg ||
      'Explore progress towards the 2030 Agenda. Click on each goal for the Kosovo EU Sustainable Development Goal indicator data.')
    : (generalData?.report_subtitle_global_sdg ||
      'Explore progress towards the 2030 Agenda. Click on each goal for the Kosovo UN Sustainable Development Goal indicator data.');
  const reportDownloadTitle = isEU
    ? (generalData?.report_download_title_eu_sdg || 'Download Kosovo EU SDG Profile (PDF)')
    : (generalData?.report_download_title_global_sdg || 'Download Kosovo SDG Profile (PDF)');
  const profileTitle = isEU ? (generalData?.kosovo_sdg_profile_title_eu_sdg || 'Kosovo SDG Profile (EU)') : (generalData?.kosovo_sdg_profile_title_global_sdg || 'Kosovo SDG Profile');
  const profileSubtitle = isEU ? (generalData?.kosovo_sdg_profile_subtitle_eu_sdg || '') : (generalData?.kosovo_sdg_profile_subtitle_global_sdg || '');
  const indexScoreTitle = isEU ? (generalData?.eu_sdg_index_score_title || 'SDG Index Score') : (generalData?.global_sdg_index_score_title || 'SDG Index Score');
  const indexScoreValueRaw = isEU ? (generalData?.eu_sdg_index_score_value || '') : (generalData?.global_sdg_index_score_value || '');
  const indexScoreValue = indexScoreValueRaw ? Number(indexScoreValueRaw).toFixed(2) : '';
  const indexRankTitle = isEU ? (generalData?.eu_sdg_index_rank_title || 'SDG Index Rank') : (generalData?.global_sdg_index_rank_title || 'SDG Index Rank');

  // Compute SDG index rank value/out-of based on peer scores list length
  const indexRankValueRaw = isEU ? (generalData?.eu_sdg_index_rank_value) : (generalData?.global_sdg_index_rank_value);
  const indexRankValue = Array.isArray(indexRankValueRaw) ? (indexRankValueRaw[0] ?? '') : indexRankValueRaw;
  const peerScores = isEU ? (generalData?.eu_peer_scores) : (generalData?.global_peer_scores);
  const indexRankOutOf = Array.isArray(peerScores) ? peerScores.length : '';
  const indexRankDisplay = indexRankValue && indexRankOutOf ? `${indexRankValue}/${indexRankOutOf}` : '';

  const homeCopy = useMemo(() => buildHomeCopy(generalData, goalType), [generalData, goalType]);

  const sharedProps = {
    goals,
    goalType,
    indicators,
    indicatorsLoading,
    title,
    subtitle,
    profileTitle,
    profileSubtitle,
    indexScoreTitle,
    indexScoreValue,
    indexRankTitle,
    indexRankDisplay,
    reportDownloadTitle,
    homeCopy,
    handleGoalClick,
    handleIndicatorClick
  };

  return (
    <>
      <GoalGridDesktop {...sharedProps} />
      <GoalGridMobile {...sharedProps} />
    </>
  );
};

export default GoalGrid;